import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { useTranslation } from 'helpers/hooks';
import { config } from 'services/Config';

import { isPinData, SelectedObject } from '../interfaces';
import { isRide, Ride } from '../../../types/ride';
import { isMarker, MarkerStatus } from '../../../types/marker';
import {
  createMarker,
  deleteMarker,
  setSelectedObject,
  updateMarker,
} from '../../../store/map/actions';
import { Button } from '../../styled/form';
import { AppState } from '../../../store/interfaces';
import Modal from '../../Modal';
import { LoadingProp } from '../../../core/settings/styled';
import { sendEvent } from '../../../services/reactGa';

import { RideDetails } from './RideDetails';
import { MarkersForm } from './MarkersForm';

const Wrapper = styled.div`
  position: relative;
  background: ${({ theme }) => theme.map.pinDetails.background};
  border-radius: 16px 16px 0 0;
  width: 100%;
  flex: 0;
  font-size: 16px;

  p {
    margin: 8px 0;
  }

  > svg {
    position: relative;
    left: 50%;
    top: 4px;
    transform: translate(-50%);
  }

  .button-sm {
    padding: 8px;
    margin-right: 8px;
    font-size: 16px;
    letter-spacing: -0.4px;
  }

  .button-skip {
    color: ${({ theme }) => theme.map.skip.color};
    background: ${({ theme }) => theme.map.skip.background};
  }

  .button-apply {
    background: ${({ theme }) => theme.map.pin.apply.background};
    color: ${({ theme }) => theme.map.pin.apply.color};
  }

  .button-sm:last-child {
    margin-right: 0;
  }
`;

const Container = styled.div`
  padding: 0 8px 12px;
`;

interface ModalDataProps {
  selectedObject: SelectedObject;
  isEditMode: boolean;
  onDropRide?(id: number): void;
  setModal(modal: React.ReactElement | null): void;
  rides?: Ride[];
}

interface ModalProps {
  isEditMode: boolean;
  onDropRide?(id: number): void;
  rides?: Ride[];
}

const Details: React.FC<ModalDataProps> = React.memo(function ({
  selectedObject,
  onDropRide,
  setModal,
  rides,
}) {
  const { isCreatingMarker, touchingMarkers, markers } = useSelector(
    (app: AppState) => ({
      touchingMarkers: app.map.touchingMarkers,
      isCreatingMarker: app.map.isCreatingMarker,
      markers: app.map.markers,
    }),
    shallowEqual
  );

  const maxActiveMarkers = config.getItem('maxActiveMarkers');
  const activeMarkers = markers.list.filter((marker) => marker.status !== MarkerStatus.inactive);

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const closeModal = useCallback(() => setModal(null), []);

  const handleCreateMarker = () => {
    if (!isPinData(selectedObject)) {
      return;
    }

    const { lat, lng } = selectedObject.position;
    dispatch(createMarker.request({ lat, lng }));
    closeModal();
  };

  const handleAddMarker = () => {
    if (!isPinData(selectedObject)) {
      return;
    }

    sendEvent('Map_CreatePin');

    if (activeMarkers.length === maxActiveMarkers) {
      setModal(
        <Modal
          onClose={closeModal}
          onApply={handleCreateMarker}
          applyText={t('components.modal.yes')}
        >
          {t('map.replaceActivePin')}
          <MarkersForm markers={activeMarkers} />
        </Modal>
      );
      return;
    }

    handleCreateMarker();
  };

  const changePinStatus = (status: MarkerStatus) => {
    if (!isMarker(selectedObject)) {
      return;
    }

    const applyMarkerStatus = () => {
      const { id, lat, lng, address } = selectedObject;
      const formData = new URLSearchParams();

      formData.append('lat', lat);
      formData.append('lng', lng);
      formData.append('address', address);
      formData.append('status', `${status}`);

      sendEvent(status === MarkerStatus.active ? 'Map_ActivePin' : 'Map_InactivatePin');

      dispatch(updateMarker.request(id, formData));
      closeModal();
    };

    const content = (function () {
      if (status === MarkerStatus.inactive) {
        return t('map.makeInActivePinModal');
      }

      if (activeMarkers.length < maxActiveMarkers) {
        return t('map.makeActivePinModal');
      }

      return (
        <>
          {t('map.replaceActivePin')}
          <MarkersForm markers={activeMarkers} />
        </>
      );
    })();

    setModal(
      <Modal onClose={closeModal} onApply={applyMarkerStatus} applyText={t('components.modal.yes')}>
        {content}
      </Modal>
    );
  };

  const handleDeleteMarker = useCallback(() => {
    if (!isMarker(selectedObject)) {
      return;
    }

    const deletePin = () => {
      sendEvent('Map_DeletePin');
      dispatch(deleteMarker.request(selectedObject.id));
      closeModal();
    };

    setModal(
      <Modal applyText={t('components.modal.yes')} onApply={deletePin} onClose={closeModal}>
        {t('map.deletePinModal')}
      </Modal>
    );
  }, [selectedObject]);

  const onCancel = useCallback(() => dispatch(setSelectedObject(null)), []);

  if (!selectedObject) {
    return null;
  }

  if (isRide(selectedObject)) {
    return <RideDetails onCancel={onCancel} selectedObject={selectedObject} rides={rides} />;
  }

  if (isMarker(selectedObject)) {
    if (selectedObject.status === 0) {
      return (
        <>
          <p>{selectedObject.address}</p>
          <div>
            {touchingMarkers[selectedObject.id] ? (
              <LoadingProp />
            ) : (
              <>
                <Button
                  className="button-sm button-apply"
                  onClick={() => changePinStatus(MarkerStatus.active)}
                >
                  {t('map.makeActive')}
                </Button>
                <Button className="button-sm button-skip" onClick={handleDeleteMarker}>
                  {t('map.delete')}
                </Button>
                <Button className="button-sm" onClick={onCancel}>
                  {t('map.cancel')}
                </Button>
              </>
            )}
          </div>
        </>
      );
    }
    return (
      <>
        <p>{selectedObject.address}</p>
        <div>
          {touchingMarkers[selectedObject.id] ? (
            <LoadingProp />
          ) : (
            <>
              {activeMarkers.length > 1 && (
                <>
                  {/* <Button
                    className="button-sm button-apply"
                    onClick={() => changePinStatus(MarkerStatus.inactive)}
                  >
                    {t('map.makeInactive')}
                  </Button> */}
                  <Button className="button-sm button-skip" onClick={handleDeleteMarker}>
                    {t('map.delete')}
                  </Button>
                </>
              )}
              <Button className="button-sm" onClick={onCancel}>
                {t('map.cancel')}
              </Button>
            </>
          )}
        </div>
      </>
    );
  }

  if (!isPinData(selectedObject)) {
    return null;
  }

  if (selectedObject.isDriverCoords) {
    return <div />;
  }

  return (
    <div>
      <p>{selectedObject.address}</p>
      {isCreatingMarker ? (
        <LoadingProp />
      ) : (
        <>
          <Button className="button-sm button-apply" onClick={handleAddMarker}>
            {t('map.addPin')}
          </Button>
          <Button className="button-sm" onClick={onCancel}>
            {t('map.cancel')}
          </Button>
        </>
      )}
    </div>
  );
});

export const PinDetails: React.FC<ModalProps> = React.memo(function ({
  isEditMode,
  onDropRide,
  rides,
}) {
  const selectedObject = useSelector((app: AppState) => app.map.selectedObject, shallowEqual);
  const [modal, setModal] = useState<React.ReactElement | null>(null);

  if (!selectedObject) {
    return null;
  }

  return (
    <Wrapper id="map-details-wrapper">
      {modal}
      <Container>
        <Details
          isEditMode={isEditMode}
          setModal={setModal}
          onDropRide={onDropRide}
          selectedObject={selectedObject}
          rides={rides}
        />
      </Container>
    </Wrapper>
  );
});
